import { IListItemType } from "./configHelper";
import { degrees, PDFDocument, rgb, StandardFonts } from "pdf-lib";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import { format } from "date-fns";
import { roundWidth, squareHeight, squareWidth } from "./mathHelpers";
import { IElementType } from "../definitions/element";
import {
  ADDITIONAL_TYPES,
  IAddtionalCloud,
  IAddtionalElements,
} from "../definitions/additional";
import { t } from "./userHelper";
import { dictionary } from "../untils/dictionary";
import { ListSectionProps } from "../types/sectionTypes";
import { ISpoolCanvas, ISpoolElement } from "../definitions/spools";
import { getSpoolsForCanvas } from "./pdfHelpers";

interface IExportDataFormat {
  [key: string]: number | string | boolean;
}


export const prepareElements = (elements: IElementType[]) => {
  return elements;
};
export const xlsxListGenrator = (
  elements: IElementType[],
  headers: IListItemType[],
  projectName: string,
  sections: ListSectionProps[]
) => {
  const tableHeaders = headers;
  const tableElements = prepareElements(elements);
  const data: IExportDataFormat[] = [];

  tableElements.forEach((_item) => {
    const row: IExportDataFormat = {};
    tableHeaders.forEach((header) => {
      const value = _item[header.valueSource as keyof typeof _item];
      if (header.date) {
        row[header.name] = value
          ? format(new Date(value as number), "dd-MM-yyyy")
          : "";
      } else {
        row[header.name] = value ? (value as string | number) : "";
      }
    });

    data.push(row);
  });
  const sheetHeaders: string[] = [];

  tableHeaders.forEach((headItem) => {
    sheetHeaders.push(headItem.name);
  });
  const fileName = `${projectName}_${format(new Date(), "dd-MM-yyyy_HH:mm")}`;
  const ws = XLSX.utils.book_new();
  XLSX.utils.sheet_add_aoa(ws, [sheetHeaders]);
  XLSX.utils.sheet_add_json(ws, data, { origin: "A2", skipHeader: true });
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });
  const finalData = new Blob([excelBuffer], { type: "xlsx" });
  FileSaver.saveAs(finalData, `${fileName}.xlsx`);
};

const color = rgb(1, 0, 0);

export const getRotatedDimensions = (
  width: number,
  height: number,
  rotation: number
) => {
  if (
    rotation === 90 ||
    rotation === -270 ||
    rotation === 270 ||
    rotation === -90
  ) {
    return { width: height, height: width };
  }
  return { width, height };
};

export const setDimensions = (x: number, y: number, rotation: number) => {
  const newCord = { x: 0, y: 0 };
  switch (rotation) {
    case 90:
    case -270:
      //ok
      newCord.x = -y;
      newCord.y = -x;
      break;
    case 180:
    case -180:
      //ok
      newCord.x = x;
      newCord.y = -y;
      break;
    case -90:
    case 270:
      //ok
      newCord.x = y;
      newCord.y = x;
      break;
    case 0:
    default:
      //ok
      newCord.x = -x;
      newCord.y = y;
      break;
  }
  return newCord;
};

export const setSpoolLabelCord = (
  x: number,
  y: number,

  rotation: number
) => {
  const newCord = { x: 0, y: 0 };
  switch (rotation) {
    case 90:
    case -270:
      newCord.x = x - 18;
      newCord.y = y - 48;
      break;
    case 180:
    case -180:
      newCord.x = x - 48;
      newCord.y = y - 18;
      break;
    case -90:
    case 270:
      newCord.x = x - 18;
      newCord.y = y - 48;
      break;
    case 0:
    default:
      newCord.x = x - 48;
      newCord.y = y - 18;
      break;
  }
  return newCord;
};
export const setNewCord = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotation: number
) => {
  const { width: newWidth, height: newHeight } = getRotatedDimensions(
    width,
    height,
    rotation
  );
  const newCord = { x: 0, y: 0 };

  switch (rotation) {
    case 90:
    case -270:
      newCord.x = y;
      newCord.y = x;
      break;
    case 180:
    case -180:
      newCord.x = newWidth - x;
      newCord.y = y;
      break;
    case -90:
    case 270:
      newCord.x = newHeight - y;
      newCord.y = newWidth - x;
      break;
    case 0:
    default:
      newCord.x = x;
      newCord.y = newHeight - y;
      break;
  }
  return newCord;
};

export const setLabelTextXCord = (
  rotation: number,
  startPoint: number,
  textWidth: number
) => {
  let xCord = startPoint - textWidth / 2;
  if (rotation === 90 || rotation === -270) {
    xCord = startPoint + 5;
  } else if (rotation === 180) {
    xCord = startPoint + textWidth / 2;
  } else if (rotation === -90 || rotation === 270) {
    xCord = startPoint - 5;
  }
  return xCord;
};

export const setLabelTextYCord = (
  rotation: number,
  startPoint: number,
  textWidth: number
) => {
  let yCord = startPoint - 5;
  if (rotation === 90 || rotation === -270) {
    yCord = startPoint - textWidth / 2;
  } else if (rotation === 180) {
    yCord = startPoint + 5;
  } else if (rotation === -90 || rotation === 270) {
    yCord = startPoint + textWidth / 2;
  }
  return yCord;
};

export const pdfMapGenerator = async (
  fileUrl: string,
  fileName: string,
  elements: IElementType[],
  additional: IAddtionalElements[],
  spools: ISpoolElement[],
  sectionID: string
) => {
  await fetch(fileUrl)
    .then((res) => res.arrayBuffer())
    .then(async (arrayBufferData) => {
      const pdfDoc = await PDFDocument.load(arrayBufferData);
      const pages = pdfDoc.getPages();
      const contName = await pdfDoc.embedFont(StandardFonts.Helvetica);
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const filteredElements = elements.filter(
          (item) => item.page === i + 1 && item.sectionID === sectionID
        );
        const filteredAdditional = additional.filter(
          (item) => item.page === i + 1 && item.sectionID === sectionID
        );
        const filteredSpools = getSpoolsForCanvas(
          spools as any,
          sectionID,
          i + 1
        );


        const rotation = page.getRotation().angle;
        const pageHeight = page.getHeight();
        const pageWidth = page.getWidth();

        const fontSize = 14;
        let textWidth = 0;
        if (filteredAdditional.length > 0) {
          filteredAdditional.forEach((element) => {
            switch (element.type) {
              case ADDITIONAL_TYPES.dot:
                const dotCordinates = setDotCordinates(
                  element,
                  pageWidth,
                  pageHeight,
                  rotation
                );
                page.drawEllipse({
                  x: dotCordinates.x,
                  y: dotCordinates.y,
                  xScale: element.size / 2,
                  yScale: element.size / 2,
                  color: color,
                });
                break;
              case ADDITIONAL_TYPES.edge: // ToDo ogólnie ok, minor fixes needed
                const edgeCordinates = setEdgeCordinates(
                  element,
                  pageWidth,
                  pageHeight,
                  rotation
                );
                page.drawLine({
                  color: color,
                  start: {
                    x: (edgeCordinates.x as number) + element.height / 2,
                    y: edgeCordinates.y as number,
                  },
                  end: {
                    x: edgeCordinates.end_x + element.height / 2,
                    y: edgeCordinates.end_y,
                  },
                  thickness: element.height,
                  opacity: 1,
                });
                break;
              case ADDITIONAL_TYPES.triage:
                const traigeCordinates = setTriageCordinates(
                  element,
                  pageWidth,
                  pageHeight,
                  rotation
                );

                const pathString = rotateSvgPath(
                  traigeCordinates.points,
                  element.rotation
                );

                page.drawSvgPath(pathString, {
                  x: traigeCordinates.startX,
                  y: traigeCordinates.startY,
                  color: color,
                  opacity: 1,
                });

                break;
              case ADDITIONAL_TYPES.cloud:
                const cloudCordinates = setCloudCordinates(
                  element as IAddtionalCloud,
                  pageWidth,
                  pageHeight,
                  rotation
                );
                const pathCloudString = generateCloudPath(
                  cloudCordinates,
                  cloudCordinates.segmentWidth,
                  cloudCordinates.segmentHeight,
                  rotation
                );

                page.drawSvgPath(pathCloudString, {
                  x: cloudCordinates.startX,
                  y: cloudCordinates.startY,
                  borderColor: color,
                  borderWidth: 2,
                });

                break;
            }
          });
        }
        if (filteredElements.length > 0) {
          filteredElements.forEach((element) => {
            const cordinates = setElementCordinates(
              element,
              pageWidth,
              pageHeight,
              rotation
            );
            page.drawEllipse({
              x: cordinates.x,
              y: cordinates.y,
              xScale: 2,
              yScale: 2,
              color: color,
            });

            page.drawLine({
              color: color,
              start: { x: cordinates.x as number, y: cordinates.y as number },
              end: { x: cordinates.line_x, y: cordinates.line_y },
              thickness: 2,
              opacity: 1,
            });

            const scale = roundWidth;
            const rectStartX = cordinates.line_x - scale / 2;
            const rectStartY = cordinates.line_y - scale / 2;
            page.drawEllipse({
              y: rectStartY,
              x: rectStartX,
              xScale: scale * 5,
              yScale: scale * 5,
              color: rgb(1, 1, 1),
              borderColor: color,
              borderWidth: 2,
            });
            try {
              textWidth = contName.widthOfTextAtSize(
                element.joint_name,
                fontSize
              );
            } catch (e) {
              console.log(e);
            }

            page.drawText(element.joint_name as string, {
              x: setLabelTextXCord(rotation, rectStartX, textWidth),
              y: setLabelTextYCord(rotation, rectStartY, textWidth),
              size: fontSize,
              rotate: degrees(rotation),
              color: rgb(0, 0, 0),
            });
          });
        }
        //spools
        if (filteredSpools.length > 0) {
          filteredSpools.forEach((element) => {
            const cordinates = setElementCordinates(
              element,
              pageWidth,
              pageHeight,
              rotation
            );
            page.drawEllipse({
              x: cordinates.x,
              y: cordinates.y,
              xScale: 2,
              yScale: 2,
              color: color,
            });

            page.drawLine({
              color: color,
              start: { x: cordinates.x as number, y: cordinates.y as number },
              end: { x: cordinates.line_x, y: cordinates.line_y },
              thickness: 2,
              opacity: 1,
            });

            const rectStartX = cordinates.line_x - squareWidth / 2;
            const rectStartY = cordinates.line_y - squareHeight / 2;

            const { width: newWidth, height: newHeight } = getRotatedDimensions(
              squareWidth,
              squareHeight,
              rotation
            );
            const labeleCords = setSpoolLabelCord(
              rectStartX,
              rectStartY,
              rotation
            );
            page.drawRectangle({
              x: labeleCords.x,
              y: labeleCords.y,
              width: newWidth * 10,
              height: newHeight * 10,
              color: rgb(1, 1, 1),
              borderColor: color,
              borderWidth: 2,
              opacity: 1, 
            });
            try {
              textWidth = contName.widthOfTextAtSize(
                element.spoolName,
                fontSize
              );
            } catch (e) {
              console.log(e);
            }

            page.drawText(element.spoolName as string, {
              x: setLabelTextXCord(rotation, rectStartX, textWidth),
              y: setLabelTextYCord(rotation, rectStartY, textWidth),
              size: fontSize,
              rotate: degrees(rotation),
              color: rgb(0, 0, 0),
            });
          });
        }
      }
      const pdfBytes = await pdfDoc.save();
      const finalData = new Blob([pdfBytes], { type: "pdf" });
      FileSaver.saveAs(finalData, `${fileName}.pdf`);
    });
};

export const setElementCordinates = (
  element: IElementType | ISpoolCanvas,
  pageWidth: number,
  pageHeight: number,
  rotation: number
) => {
  const pointCords = setNewCord(
    element.x as number,
    element.y as number,
    pageWidth,
    pageHeight,
    rotation
  );
  const labelCords = setDimensions(
    element.label_x as number,
    element.label_y as number,
    rotation
  );

  return {
    x: pointCords.x,
    y: pointCords.y,
    label_x: labelCords.x,
    label_y: labelCords.y,
    line_x: pointCords.x - labelCords.x,
    line_y: pointCords.y - labelCords.y,
  };
};

export const setDotCordinates = (
  element: IAddtionalElements,
  pageWidth: number,
  pageHeight: number,
  rotation: number
) => {
  const newCord = setNewCord(
    element.x as number,
    element.y as number,
    pageWidth,
    pageHeight,
    rotation
  );

  return {
    x: newCord.x,
    y: newCord.y,
  };
};

export const setEdgeCordinates = (
  element: IAddtionalElements,
  pageWidth: number,
  pageHeight: number,
  rotation: number
) => {
  const startCord = setNewCord(
    element.x as number,
    element.y as number,
    pageWidth,
    pageHeight,
    rotation
  );
  const endCord = setNewCord(
    element.end_x as number,
    element.end_y as number,
    pageWidth,
    pageHeight,
    rotation
  );
  return {
    x: startCord.x,
    y: startCord.y,
    end_x: endCord.x,
    end_y: endCord.y,
  };
};

export const correctVertexCordinates = (
  x: number,
  y: number,
  angle: number
) => {
  const newCordinates = {
    x: 0,
    y: 0,
  };
  switch (angle) {
    case 90:
    case -270:
      newCordinates.x = y;
      newCordinates.y = x;
      break;
    case 180:
    case -180:
      newCordinates.x = -x;
      newCordinates.y = -y;
      break;
    case -90:
    case 270:
      newCordinates.x = -y;
      newCordinates.y = -x;
      break;
    case 0:
    default:
      newCordinates.x = x;
      newCordinates.y = y;
      break;
  }

  return [newCordinates.x, newCordinates.y];
};

export const setTriageCordinates = (
  element: IAddtionalElements,
  pageWidth: number,
  pageHeight: number,
  rotation: number
) => {
  const size = element.size;
  const triageCenter = setDotCordinates(
    element,
    pageWidth,
    pageHeight,
    rotation
  );

  const vertexA = {
    x: 0,
    y: -size / 2,
  };
  const vertexB = {
    x: size / 2,
    y: size / 2,
  };
  const vertexC = {
    x: -(size / 2),
    y: size / 2,
  };

  const vertexACord = correctVertexCordinates(vertexA.x, vertexA.y, rotation);
  const vertexBCord = correctVertexCordinates(vertexB.x, vertexB.y, rotation);
  const vertexCCord = correctVertexCordinates(vertexC.x, vertexC.y, rotation);
  const points = [vertexACord, vertexBCord, vertexCCord];
  return {
    startX: triageCenter.x,
    startY: triageCenter.y,
    points,
  };
};

export const setCloudCordinates = (
  element: IAddtionalCloud,
  pageWidth: number,
  pageHeight: number,
  rotation: number
): IEdgeObject => {
  const sector = 15;

  const fullWidth = element.size;
  const fullHeight = element.height;

  const cloudCenter = setDotCordinates(
    element as IAddtionalElements,
    pageWidth,
    pageHeight,
    rotation
  );
  const centerX = cloudCenter.x;
  const centerY = cloudCenter.y;

  const availableWidth = fullWidth - sector * 2;
  const availableHeight = fullHeight - sector * 2;
  const factorWidth = parseFloat(
    Math.floor(availableWidth / sector).toFixed(2)
  );
  const factorHeight = parseFloat(
    Math.floor(availableHeight / sector).toFixed(2)
  );
  const segmentWidth = parseFloat((availableWidth / factorWidth).toFixed(2));
  const segmentHeight = parseFloat((availableHeight / factorHeight).toFixed(2));

  const points = getAllEdgePoints(
    centerX,
    centerY,
    fullWidth,
    fullHeight,
    element.rotation,
    segmentWidth,
    segmentHeight,
    rotation
  );
  return {
    topEdge: points.top,
    rightEdge: points.right,
    bottomEdge: points.bottom,
    leftEdge: points.left,
    startX: centerX,
    startY: centerY,
    segmentWidth: segmentWidth,
    segmentHeight: segmentHeight,
  };
};

interface IEdgePoints {
  x: number;
  y: number;
}

interface IEdgeObject {
  topEdge: IEdgePoints[];
  rightEdge: IEdgePoints[];
  bottomEdge: IEdgePoints[];
  leftEdge: IEdgePoints[];
  startX: number;
  startY: number;
  segmentWidth: number;
  segmentHeight: number;
}

function getAllEdgePoints(
  x: number,
  y: number,
  width: number,
  height: number,
  alpha: number,
  widthShift: number,
  heightShift: number,
  pageRotation: number
) {
  const vertexA = {
    x: x - width / 2 + widthShift / 2,
    y: y - height / 2 + heightShift / 2,
  };
  const vertexB = {
    x: x + width / 2 - widthShift / 2,
    y: y - height / 2 + heightShift / 2,
  };
  const vertexC = {
    x: x + width / 2 - widthShift / 2,
    y: y + height / 2 - heightShift / 2,
  };
  const vertexD = {
    x: x - width / 2 + widthShift / 2,
    y: y + height / 2 - heightShift / 2,
  };

  function getEdgePoints(
    start: { x: number; y: number },
    end: { x: number; y: number },
    shift: number
  ) {
    const points = [];
    const distance = Math.sqrt((end.x - start.x) ** 2 + (end.y - start.y) ** 2);
    const numSteps = Math.floor(distance / shift);

    for (let i = 0; i <= numSteps; i++) {
      let t = i / numSteps;
      const newX = start.x + t * (end.x - start.x) - x;
      const newY = start.y + t * (end.y - start.y) - y;
      const [rotatedX, rotatedY] = correctVertexCordinates(
        newX,
        newY,
        pageRotation
      );
      points.push(rotateVertex(rotatedX, rotatedY, alpha));
    }

    return points;
  }

  const edgePoints = {
    top: getEdgePoints(vertexA, vertexB, widthShift),
    right: getEdgePoints(vertexB, vertexC, heightShift),
    bottom: getEdgePoints(vertexC, vertexD, widthShift),
    left: getEdgePoints(vertexD, vertexA, heightShift),
  };
  return edgePoints;
}

interface IEdgePoint {
  x: number;
  y: number;
}

const generateArcPath = (
  startPoint: IEdgePoint,
  points: IEdgePoint[],
  radius: number,
  pageRotation: number
) => {
  const direction = pageRotation === 0 || pageRotation === 180 ? 1 : 0;
  let path = `A ${radius} ${radius} 0 1 ${direction} ${startPoint.x},${startPoint.y} `;
  points.forEach((point, index) => {
    if (index !== 0) {
      path += `A ${radius} ${radius} 0 1 ${direction} ${point.x},${point.y} `;
    }
  });
  return path;
};

export const generateCloudPath = (
  edgeObj: IEdgeObject,
  segmentWidth: number,
  segmentHeight: number,
  pageRotation: number
): string => {
  const radiusWidth = segmentWidth / 2;
  const radiusHeight = segmentHeight / 2;

  let pathString = `M${edgeObj.leftEdge[edgeObj.leftEdge.length - 1].x},${
    edgeObj.leftEdge[edgeObj.leftEdge.length - 1].y
  } `;

  pathString += generateArcPath(
    edgeObj.topEdge[0],
    edgeObj.topEdge,
    radiusWidth,
    pageRotation
  );
  pathString += generateArcPath(
    edgeObj.rightEdge[0],
    edgeObj.rightEdge,
    radiusHeight,
    pageRotation
  );
  pathString += generateArcPath(
    edgeObj.bottomEdge[0],
    edgeObj.bottomEdge,
    radiusWidth,
    pageRotation
  );
  pathString += generateArcPath(
    edgeObj.leftEdge[0],
    edgeObj.leftEdge,
    radiusHeight,
    pageRotation
  );

  pathString += "Z";
  return pathString;
};

const rotateVertex = (x: number, y: number, alpha: number) => {
  const alphaRad = (alpha * Math.PI) / 180;
  const rotatedX = x * Math.cos(alphaRad) - y * Math.sin(alphaRad);
  const rotatedY = x * Math.sin(alphaRad) + y * Math.cos(alphaRad);
  return {
    x: rotatedX,
    y: rotatedY,
  };
};

export const rotateSvgPath = (points: number[][], alpha: number) => {
  const rotatedPoints = points.map(([x, y]) => rotateVertex(x, y, alpha));
  const newPath =
    `M${rotatedPoints[0].x},${rotatedPoints[0].y} ` +
    rotatedPoints
      .slice(1)
      .map(({ x, y }) => `L${x},${y}`)
      .join(" ") +
    " Z";

  return newPath;
};
